/**
 * Added support of MaskSprite and MaskMesh
 */
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';
import {IUpdatable} from '@fantasmagames/core/src/update/_interfaces/IUpdatable';

export class Spine extends PIXI.spine.Spine implements IUpdatable {
	protected _updateManager: UpdateManager;
	protected _isPlaying: boolean;

	constructor(spineData: PIXI.spine.core.SkeletonData) {
		super(spineData);

		if (!__PRODUCTION__) {
			this.name = spineData.name;
		}

		this.autoUpdate = false;
	}

	public start(): void {
		this._isPlaying = true;
	}

	public stop(): void {
		this._isPlaying = false;
		this._updateManager && this._updateManager.remove(this);
	}

	public createSprite(slot: PIXI.spine.core.Slot, attachment: PIXI.spine.core.RegionAttachment, defName: string): PIXI.spine.SpineSprite {
		let region = attachment.region;

		if (slot.tempAttachment === attachment) {
			region = slot.tempRegion;
			slot.tempAttachment = null;
			slot.tempRegion = null;
		}

		const texture = region.texture;
		const sprite = this.newMaskSprite(texture, (region as any).maskTexture);

		sprite.rotation = attachment.rotation * PIXI.spine.core.MathUtils.degRad;
		sprite.anchor.x = 0.5;
		sprite.anchor.y = 0.5;
		sprite.position.x = attachment.x;
		sprite.position.y = attachment.y;
		sprite.alpha = attachment.color.a;

		sprite.region = attachment.region;
		(this as any).setSpriteRegion(attachment, sprite, attachment.region);

		slot.sprites = slot.sprites || {};
		slot.sprites[defName] = sprite;

		return sprite;
	}

	public createMesh(slot: PIXI.spine.core.Slot, attachment: PIXI.spine.core.MeshAttachment): PIXI.spine.SpineMesh {
		let region = attachment.region;

		if (slot.tempAttachment === attachment) {
			region = slot.tempRegion;
			slot.tempAttachment = null;
			slot.tempRegion = null;
		}

		const strip = this.newMaskMesh(
			region.texture,
			(region as any).maskTexture,
			new Float32Array(attachment.regionUVs.length),
			new Float32Array(attachment.regionUVs.length),
			new Uint16Array(attachment.triangles),
			PIXI.mesh.Mesh.DRAW_MODES.TRIANGLES,
		);

		strip.canvasPadding = 1.5;
		strip.alpha = attachment.color.a;
		strip.region = attachment.region;

		// private method
		(this as any).setMeshRegion(attachment, strip, region);

		slot.meshes = slot.meshes || {};
		slot.meshes[attachment.name] = strip;

		return strip;
	}

	public update(dt: number): void {
		super.update(dt / 1000);
	}

	private newMaskSprite(texture: PIXI.Texture, maskTexture: PIXI.Texture): PIXI.spine.SpineSprite {
		const sprite = new PIXI.spine.SpineSprite(texture);

		// use MaskSpriteRenderer
		if (maskTexture) {
			sprite.pluginName = 'maskSprite';
			(sprite as any).maskTexture = maskTexture;
		}

		return sprite;
	}

	private newMaskMesh(
		texture: PIXI.Texture,
		maskTexture: PIXI.Texture,
		vertices?: Float32Array,
		uvs?: Float32Array,
		indices?: Uint16Array,
		drawMode?: number,
	): PIXI.spine.SpineMesh {
		const mesh = new PIXI.spine.SpineMesh(texture, vertices, uvs, indices, drawMode);

		// use MaskSpriteRenderer
		if (maskTexture) {
			mesh.pluginName = 'maskMesh';
			(mesh as any).maskTexture = maskTexture;
		}

		return mesh;
	}

	public get updateManager(): UpdateManager {
		return this._updateManager;
	}

	public set updateManager(value: UpdateManager) {
		this._updateManager = value;
	}

	public get isPlaying(): boolean {
		return this._isPlaying;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/spine/Spine.ts